/**
 * Centralized icon re-exports — iconsax-reactjs aliased to app names.
 * Import from '@/components/icons' instead of 'lucide-react'.
 */
export {
  Add as Plus,
  Minus,
  SearchNormal1 as Search,
  Trash as Trash2,
  Edit2 as Pencil,
  More as MoreHorizontal,
  ArrowRight2 as ArrowRight,
  ArrowUp2 as ArrowUp,
  ArrowDown2 as ArrowDown,
  ArrowSwapHorizontal as ArrowRightLeft,
  ArrowSwapVertical as ChevronsUpDown,
  TickCircle as Check,
  Eye,
  EyeSlash as EyeOff,
  Setting2 as Settings,
  Logout as LogOut,
  User,
  Printer,
  Save2 as Save,
  RotateLeft as RotateCcw,
  Warning2 as AlertTriangle,
  ShoppingCart,
  ShoppingBag,
  Box1 as Package,
  BoxAdd as PackagePlus,
  People as Users,
  Truck,
  Building as Warehouse,
  Bank as Landmark,
  MoneyRecive as Banknote,
  Receipt1 as Receipt,
  BookSaved as BookCheck,
  Element3 as LayoutDashboard,
  FolderOpen,
  Folder,
  FolderAdd as FolderPlus,
  Document as File,
  DocumentText as FileDoc,
  DocumentDownload as FileDownload,
  Menu,
  ArrowLeft2 as ChevronsLeft,
  ArrowLeft2 as ArrowLeft,
  ArrowRight3 as ChevronsRight,
  Candle2 as SlidersHorizontal,
  UserAdd as UserPlus,
  UserTick as UserCheck,
  ExportSquare as Upload,
  ImportSquare as Download,
  DocumentText as FileText,
  DollarCircle as DollarSign,
  Clipboard as ClipboardList,
  TrendUp as TrendingUp,
  TrendDown as TrendingDown,
  Gallery as ImageIcon,
  Star1 as Star,
  Refresh2 as Loader2,
  Notification as Bell,
  MessageQuestion as HelpCircle,
  Call as Phone,
  Sms as Mail,
  Filter,
  Chart as BarChart3,
  Chart1 as LineChartIcon,
  Graph as Activity,
  PresentionChart as PresentationChart,
  FavoriteChart as RadarIcon,
  Element3 as GridIcon,
  Category as CategoryIcon,
  Calendar1 as Calendar,
  InfoCircle,
  MoreCircle as MoreVertical,
  Colorfilter,
  Copy,
  TextalignLeft as AlignLeft,
  TextalignCenter as AlignCenter,
  TextalignRight as AlignRight,
  VolumeHigh as Volume2,
  VolumeSlash as VolumeX,
  MagicStar as Sparkles,
} from 'iconsax-reactjs';

// Aliases for duplicates (same source, different name)
import { Box1, ArrowRight2 as _AR2, ArrowLeft2 as _AL2, ArrowDown2 as _AD2, Building as _B, More as _M, Calendar1 as _Cal } from 'iconsax-reactjs';
export const PackageSearch = Box1;
export const ChevronRight = _AR2;
export const ChevronLeft = _AL2;
export const ChevronDown = _AD2;
export const CalendarIcon = _Cal;
export const Building2 = _B;
export const GripVertical = _M;

// Database (stacked cylinders) — not in iconsax.
export function Database({ size = 24, className, ...props }: { size?: number; className?: string; [k: string]: unknown }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
      <path d="M3 12c0 1.66 4 3 9 3s9-1.34 9-3" />
    </svg>
  );
}

// Cloud/server plug icon for Supabase connection.
export function Plug({ size = 24, className, ...props }: { size?: number; className?: string; [k: string]: unknown }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
      <path d="M9 2v6" />
      <path d="M15 2v6" />
      <path d="M6 8h12v5a6 6 0 0 1-12 0V8z" />
      <path d="M12 19v3" />
    </svg>
  );
}

// Plain X icon (no circle) — iconsax only has CloseCircle/CloseSquare
export function X({ size = 24, className, ...props }: { size?: number; className?: string; [k: string]: unknown }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

// Outbound arrow (link opens Supabase dashboard in new tab).
export function ExternalLink({ size = 24, className, ...props }: { size?: number; className?: string; [k: string]: unknown }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
      <path d="M15 3h6v6" />
      <path d="M10 14 21 3" />
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    </svg>
  );
}

// Small info circle used for inline hints and tips.
export function Info({ size = 24, className, ...props }: { size?: number; className?: string; [k: string]: unknown }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
      <circle cx="12" cy="12" r="9" />
      <line x1="12" y1="11" x2="12" y2="16" />
      <circle cx="12" cy="8" r="0.5" fill="currentColor" />
    </svg>
  );
}

// Lightning bolt — upgrade / boost action.
export function Zap({ size = 24, className, ...props }: { size?: number; className?: string; [k: string]: unknown }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

// Sun icon for light theme toggle.
export function Sun({ size = 24, className, ...props }: { size?: number; className?: string; [k: string]: unknown }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </svg>
  );
}

// Moon icon for dark theme toggle.
export function Moon({ size = 24, className, ...props }: { size?: number; className?: string; [k: string]: unknown }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

// Laptop/monitor icon for system theme toggle.
export function Laptop({ size = 24, className, ...props }: { size?: number; className?: string; [k: string]: unknown }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
      <rect x="3" y="4" width="18" height="12" rx="2" ry="2" />
      <line x1="2" y1="20" x2="22" y2="20" />
    </svg>
  );
}

// Archive / ZIP box icon — used for ZIP export button.
export function Archive({ size = 24, className, ...props }: { size?: number; className?: string; [k: string]: unknown }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
      <polyline points="21 8 21 21 3 21 3 8" />
      <rect x="1" y="3" width="22" height="5" />
      <line x1="10" y1="12" x2="14" y2="12" />
    </svg>
  );
}

// Repeat / refresh-arrows icon — used for recurring payments.
export function Repeat({ size = 24, className, ...props }: { size?: number; className?: string; [k: string]: unknown }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
      <polyline points="17 1 21 5 17 9" />
      <path d="M3 11V9a4 4 0 0 1 4-4h14" />
      <polyline points="7 23 3 19 7 15" />
      <path d="M21 13v2a4 4 0 0 1-4 4H3" />
    </svg>
  );
}

// Barcode-scan icon (camera viewfinder over barcode lines) — used to trigger
// the camera-based article scanner in the picker.
export function ScanBarcode({ size = 24, className, ...props }: { size?: number; className?: string; [k: string]: unknown }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" className={className} {...props}>
      {/* Viewfinder corners */}
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      {/* Barcode lines inside */}
      <line x1="7" y1="9" x2="7" y2="15" />
      <line x1="10" y1="9" x2="10" y2="15" />
      <line x1="13" y1="9" x2="13" y2="15" />
      <line x1="17" y1="9" x2="17" y2="15" />
    </svg>
  );
}
